import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useWebSocket } from "@/hooks/useWebSocket";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Circle,
  Loader2,
  ArrowLeft,
  PlayCircle,
  User,
  Calendar,
  Timer,
} from "lucide-react";
import axios from "axios";
import {
  PipelineProgressMessage,
  PipelineStepInfo,
} from "@/contexts/WebSocketTypes";
import { logger } from "@/utils/logger";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { formatDistanceToNow } from "date-fns";

interface PipelineProps {
  apiBaseUrl: string;
}

// Pipeline phases configuration
const PIPELINE_PHASES = {
  PHASE_1: {
    id: 1,
    name: "Preparation Phase",
    description: "Data collection and analysis preparation",
    color: "bg-blue-50 border-blue-200",
    steps: [
      {
        key: "fetch",
        display_name: "Intercom Fetch",
        description: "Fetch latest articles from Intercom",
        api_key: "run_fetch",
      },
      {
        key: "preprocess",
        display_name: "Preprocessing",
        description: "Run parsing and processing",
        api_key: "run_preprocess",
      },
      {
        key: "embeddings",
        display_name: "Embedding Generation",
        description: "Generate embeddings for content",
        api_key: "run_embeddings",
      },
      {
        key: "analyze",
        display_name: "Similarity Analysis",
        description: "Run similarity analysis",
        api_key: "run_analyze",
      },
      {
        key: "prepare_queries",
        display_name: "Prepare AI Queries",
        description: "Prepare AI analysis queries",
        api_key: "run_prepare_queries",
      },
    ],
  },
  PHASE_2: {
    id: 2,
    name: "Execution Phase",
    description: "Execute analysis and populate results",
    color: "bg-green-50 border-green-200",
    steps: [
      {
        key: "execute_queries",
        display_name: "Execute AI Queries",
        description: "Execute AI analysis queries",
        api_key: "run_execute_queries",
      },
      {
        key: "populate_db",
        display_name: "Populate Database",
        description: "Populate MongoDB database",
        api_key: "run_populate_db",
      },
    ],
  },
};

// Flatten all steps for backward compatibility
const PIPELINE_STEPS = [
  ...PIPELINE_PHASES.PHASE_1.steps,
  ...PIPELINE_PHASES.PHASE_2.steps,
];

interface PipelineTriggerOptions {
  run_fetch?: boolean;
  use_optimized_fetch?: boolean;
  run_preprocess?: boolean;
  run_embeddings?: boolean;
  run_analyze?: boolean;
  run_prepare_queries?: boolean;
  run_execute_queries?: boolean;
  run_populate_db?: boolean;
  run_all?: boolean;
  store_pipeline_data?: boolean;
}

// Analysis Query interfaces based on provided schema
interface AnalysisQuery {
  query_id: string;
  release_note_id: string;
  release_item_title: string;
  release_item_version: string;
  article_id: string;
  article_title: string;
  ai_config: {
    provider: string;
    model: string;
    temperature: number;
    max_output_tokens: number | null;
    ai_system_instructions: string;
    additional_config: Record<string, any>;
  };
  prompt: string;
  estimated_input_tokens: number;
  estimated_cost: number;
  timestamp_created: string;
}

interface PipelinePhaseStatus {
  phase_1_completed: boolean;
  phase_2_completed: boolean;
  analysis_queries_available: boolean;
}

interface PipelineRunStep {
  step_name: string;
  status: string;
  message?: string;
  progress?: number;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  error?: string;
  timestamp: string;
}

interface PipelineRunLog {
  level: string;
  message: string;
  timestamp: string;
  source: string;
}

interface PipelineRunHistoryItem {
  id?: string;
  _id: string;
  user_id: string;
  username: string;
  arguments: string[];
  triggered_at: string;
  status: string;
  current_step?: string;
  steps: PipelineRunStep[];
  logs: PipelineRunLog[];
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  exit_code?: number;
  error?: string;
  stored_data?: {
    analysis_queries?: AnalysisQuery[];
    stored_at?: string;
    queries_count?: number;
  };
}

// Analysis Queries Display Component
function AnalysisQueriesDisplay({
  queries,
  onClose,
  showCloseButton = true,
}: {
  queries: AnalysisQuery[];
  onClose: () => void;
  showCloseButton?: boolean;
}) {
  const totalCost = queries.reduce(
    (sum, query) => sum + query.estimated_cost,
    0,
  );
  const totalTokens = queries.reduce(
    (sum, query) => sum + query.estimated_input_tokens,
    0,
  );

  // Group queries by release version
  const queriesByVersion = queries.reduce(
    (acc, query) => {
      const version = query.release_item_version || "Unknown";
      if (!acc[version]) {
        acc[version] = [];
      }
      acc[version].push(query);
      return acc;
    },
    {} as Record<string, AnalysisQuery[]>,
  );

  // Sort versions in descending order (newest first)
  const sortedVersions = Object.keys(queriesByVersion).sort((a, b) => {
    if (a === "Unknown") return 1;
    if (b === "Unknown") return -1;
    return b.localeCompare(a, undefined, { numeric: true });
  });

  const openQueryModal = (query: AnalysisQuery) => {
    const modal = document.getElementById(
      `query_modal_${query.query_id}`,
    ) as HTMLDialogElement;
    modal?.showModal();
  };

  return (
    <div className="rounded-lg border border-base-200 bg-base-100 shadow-sm">
      <div className="flex items-center justify-between border-b border-base-200 p-4">
        <div>
          <h3 className="text-md font-semibold text-base-content">
            Preparation Completed
          </h3>
          <p className="text-xs text-base-content/70">
            Review the {queries.length} generated queries before proceeding to
            execution.
          </p>
        </div>
        {showCloseButton && (
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            ✕
          </button>
        )}
      </div>

      <div className="p-4">
        {/* Summary Stats */}
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-primary/10 p-3">
            <div className="text-2xl font-bold text-base-content/70">
              {queries.length}
            </div>
            <div className="text-sm text-base-content/70">Total Queries</div>
          </div>
          <div className="rounded-lg bg-success/10 p-3">
            <div className="text-2xl font-bold text-base-content/70">
              {totalTokens.toLocaleString()}
            </div>
            <div className="text-sm text-base-content/70">Est. Tokens</div>
          </div>
          <div className="rounded-lg bg-warning/10 p-3">
            <div className="text-2xl font-bold text-base-content/70">
              ${totalCost.toFixed(3)}
            </div>
            <div className="text-sm text-base-content/70">Est. Cost</div>
          </div>
        </div>

        {/* Queries Table */}
        <div className="overflow-x-auto">
          <table className="table-pin-rows table-pin-cols table table-xs">
            <thead>
              <tr>
                <th></th>
                <td>Version</td>
                <td>Release Item</td>
                <td>Article</td>
                <td>Model</td>
                <td>Tokens</td>
                <td>Cost</td>
              </tr>
            </thead>
            <tbody>
              {sortedVersions.flatMap((version, versionIndex) => {
                const versionQueries = queriesByVersion[version];
                const versionRows = [];

                // Add version header row
                versionRows.push(
                  <tr
                    key={`header-${version}`}
                    className="border-none outline-none"
                  >
                    <th colSpan={7} className="text-md pt-4 pb-2 font-bold">
                      {version} ({versionQueries.length} queries)
                    </th>
                  </tr>,
                );

                // Add query rows for this version
                let queryCounter = 1;
                versionQueries.forEach((query, queryIndex) => {
                  const isLastQueryInVersion =
                    queryIndex === versionQueries.length - 1;
                  versionRows.push(
                    <tr
                      key={query.query_id}
                      className={`cursor-pointer hover:bg-base-200 ${
                        isLastQueryInVersion ? "border-b-0" : ""
                      }`}
                      onClick={() => openQueryModal(query)}
                    >
                      <th>{queryCounter++}</th>
                      <td>
                        <div className="badge-outline badge badge-sm">
                          {version}
                        </div>
                      </td>
                      <td>
                        <div className="max-w-xs truncate font-medium">
                          {query.release_item_title}
                        </div>
                      </td>
                      <td>
                        <div className="max-w-xs truncate">
                          {query.article_title}
                        </div>
                      </td>
                      <td>
                        <div className="text-xs">{query.ai_config.model}</div>
                      </td>
                      <td>
                        <div className="text-xs">
                          {query.estimated_input_tokens.toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <div className="text-xs">
                          ${query.estimated_cost.toFixed(3)}
                        </div>
                      </td>
                    </tr>,
                  );
                });

                // Add blank separator row (except after the last version)
                if (versionIndex < sortedVersions.length - 1) {
                  versionRows.push(
                    <tr key={`separator-${version}`} className="border-b-0">
                      <td colSpan={7} className="border-0"></td>
                    </tr>,
                  );
                }

                return versionRows;
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} className="border-0"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Query Detail Modals */}
        {queries.map((query) => (
          <dialog
            key={query.query_id}
            id={`query_modal_${query.query_id}`}
            className="modal"
          >
            <div className="modal-box max-w-4xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">Query Details</h3>
                <form method="dialog">
                  <button className="btn btn-ghost btn-sm">✕</button>
                </form>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-base-content/70">
                      Release Item
                    </label>
                    <div className="text-sm font-medium">
                      {query.release_item_title}
                    </div>
                    <div className="text-xs text-base-content/70">
                      Version: {query.release_item_version || "Unknown"}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-base-content/70">
                      Article
                    </label>
                    <div className="text-sm font-medium">
                      {query.article_title}
                    </div>
                    <div className="text-xs text-base-content/70">
                      ID: {query.article_id}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-base-content/70">
                      AI Configuration
                    </label>
                    <div className="text-sm">{query.ai_config.model}</div>
                    <div className="text-xs text-base-content/70">
                      Temperature: {query.ai_config.temperature}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-base-content/70">
                      Estimates
                    </label>
                    <div className="text-sm">
                      {query.estimated_input_tokens.toLocaleString()} tokens
                    </div>
                    <div className="text-xs text-base-content/70">
                      Cost: ${query.estimated_cost.toFixed(3)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-base-content/70">
                    AI System Instructions
                  </label>
                  <div className="mt-1 max-h-32 overflow-y-auto rounded bg-base-200 p-3 font-mono text-xs whitespace-pre-wrap">
                    {query.ai_config.ai_system_instructions}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-base-content/70">
                    Prompt
                  </label>
                  <div className="mt-1 max-h-64 overflow-y-auto rounded bg-base-200 p-3 font-mono text-xs whitespace-pre-wrap">
                    {query.prompt}
                  </div>
                </div>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop">
              <button>close</button>
            </form>
          </dialog>
        ))}
      </div>
    </div>
  );
}

// Vertical Timeline Component
interface PipelineTimelineProps {
  stepStates: Record<string, PipelineStepInfo>;
  currentPhase: 1 | 2 | null;
  phaseStatus: PipelinePhaseStatus;
}

function PipelineTimeline({
  stepStates,
  currentPhase,
  phaseStatus,
}: PipelineTimelineProps) {
  const getPhaseIcon = (phaseId: 1 | 2) => {
    const isCompleted =
      phaseId === 1
        ? phaseStatus.phase_1_completed
        : phaseStatus.phase_2_completed;
    const isCurrent = currentPhase === phaseId;

    if (isCompleted) {
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    } else if (isCurrent) {
      return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    } else {
      return <Circle className="h-5 w-5 text-base-content/30" />;
    }
  };

  const getStepIcon = (step: string) => {
    const stepState = stepStates[step];
    const status = stepState?.status || "pending";

    if (status === "completed") {
      return <CheckCircle2 className="h-4 w-4 text-success" />;
    } else if (status === "error") {
      return <XCircle className="h-4 w-4 text-error" />;
    } else if (status === "started" || status === "running") {
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    } else {
      return <Circle className="h-4 w-4 text-base-content/30" />;
    }
  };

  return (
    <div className="w-72 border-r border-base-200 bg-base-100 p-4">
      <div className="space-y-6 select-none">
        {/* Phase 1 */}
        <div className="relative">
          <div className="mb-3 flex items-center gap-3">
            {getPhaseIcon(1)}
            <div>
              <h4 className="text-sm font-medium">
                {PIPELINE_PHASES.PHASE_1.name}
              </h4>
              <p className="text-xs text-base-content/70">
                {PIPELINE_PHASES.PHASE_1.description}
              </p>
            </div>
          </div>

          {/* Phase 1 Steps */}
          <div className="ml-6 space-y-3 border-l-2 border-base-200 pl-3">
            {PIPELINE_PHASES.PHASE_1.steps.map((step) => {
              const stepState = stepStates[step.key];

              return (
                <div key={step.key} className="relative">
                  <div className="flex items-start gap-2">
                    {getStepIcon(step.key)}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">
                        {step.display_name}
                      </div>
                      <div className="text-xs text-base-content/70">
                        {stepState?.message || step.description}
                      </div>
                      {stepState?.progress && stepState.progress > 0 && (
                        <div className="mt-1">
                          <div className="h-1 rounded-full bg-base-300">
                            <div
                              className="h-1 rounded-full bg-primary transition-all"
                              style={{ width: `${stepState.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Phase 2 */}
        <div className="relative">
          <div className="mb-3 flex items-center gap-3">
            {getPhaseIcon(2)}
            <div>
              <h4 className="text-sm font-medium">
                {PIPELINE_PHASES.PHASE_2.name}
              </h4>
              <p className="text-xs text-base-content/70">
                {PIPELINE_PHASES.PHASE_2.description}
              </p>
            </div>
          </div>

          {/* Phase 2 Steps */}
          <div
            className={`ml-6 space-y-3 border-l-2 border-base-200 pl-3 ${
              !phaseStatus.phase_1_completed ? "opacity-50" : ""
            }`}
          >
            {PIPELINE_PHASES.PHASE_2.steps.map((step) => {
              const stepState = stepStates[step.key];

              return (
                <div key={step.key} className="relative">
                  <div className="flex items-start gap-2">
                    {getStepIcon(step.key)}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">
                        {step.display_name}
                      </div>
                      <div className="text-xs text-base-content/70">
                        {stepState?.message || step.description}
                      </div>
                      {stepState?.progress && stepState.progress > 0 && (
                        <div className="mt-1">
                          <div className="h-1 rounded-full bg-base-300">
                            <div
                              className="h-1 rounded-full bg-success transition-all"
                              style={{ width: `${stepState.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Pipeline({ apiBaseUrl }: PipelineProps) {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lastMessage } = useWebSocket();
  const { setBreadcrumbs } = useBreadcrumbs();

  // Determine if this is a new pipeline or viewing existing one
  const isNewPipeline = pipelineId === "new";

  // Pipeline state
  const [stepStates, setStepStates] = useState<
    Record<string, PipelineStepInfo>
  >({});
  const [isRunning, setIsRunning] = useState(false);
  const [currentPipelineId, setCurrentPipelineId] = useState<string | null>(
    null,
  );

  // Determine actual pipeline title based on state
  const pipelineTitle = currentPipelineId
    ? `Pipeline Run ${currentPipelineId.slice(0, 8)}...`
    : isNewPipeline
      ? "New Pipeline Run"
      : `Pipeline Run ${pipelineId}`;
  const [logs, setLogs] = useState<
    Array<{
      timestamp: string;
      message: string;
      level: string;
    }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  // Phase-based state
  const [phaseStatus, setPhaseStatus] = useState<PipelinePhaseStatus>({
    phase_1_completed: false,
    phase_2_completed: false,
    analysis_queries_available: false,
  });
  const [analysisQueries, setAnalysisQueries] = useState<AnalysisQuery[]>([]);
  const [currentPhase, setCurrentPhase] = useState<1 | 2 | null>(null);

  // Historical pipeline data
  const [pipelineData, setPipelineData] =
    useState<PipelineRunHistoryItem | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize step states
  useEffect(() => {
    const initialStates: Record<string, PipelineStepInfo> = {};
    PIPELINE_STEPS.forEach((step) => {
      initialStates[step.key] = {
        step: step.key,
        display_name: step.display_name,
        status: "pending",
        message: step.description,
      };
    });
    setStepStates(initialStates);
  }, []);

  // Set breadcrumbs based on actual pipeline state
  useEffect(() => {
    const displayId = currentPipelineId || pipelineId;
    const breadcrumbLabel = currentPipelineId
      ? `Pipeline ${currentPipelineId.slice(0, 8)}...`
      : isNewPipeline
        ? "New Pipeline Run"
        : `Pipeline ${pipelineId}`;

    setBreadcrumbs([
      { label: "Pipeline Runs", href: "/pipeline" },
      {
        label: breadcrumbLabel,
        href: displayId !== "new" ? `/pipeline/${displayId}` : `/pipeline/new`,
      },
    ]);
  }, [setBreadcrumbs, isNewPipeline, pipelineId, currentPipelineId]);

  // Fetch pipeline data if viewing existing pipeline
  useEffect(() => {
    if (!isNewPipeline && pipelineId && token) {
      setLoading(true);
      axios
        .get<PipelineRunHistoryItem>(
          `${apiBaseUrl}/pipeline/history/${pipelineId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        .then((response) => {
          const run = response.data;
          setPipelineData(run);
          setCurrentPipelineId(run._id);

          // Check if this pipeline is actively running
          const isActivelyRunning =
            run.status === "running" || run.status === "started";

          // Parse arguments to determine selected steps
          const argumentsToSteps = new Set<string>();
          run.arguments.forEach((arg) => {
            const step = PIPELINE_STEPS.find(
              (s) => `--${s.key.replace("_", "-")}` === arg,
            );
            if (step) {
              argumentsToSteps.add(step.key);
            }
            // Handle special cases
            if (arg === "--fetch") argumentsToSteps.add("fetch");
            if (arg === "--use-optimized-fetch")
              argumentsToSteps.add("use_optimized_fetch");
            if (arg === "--preprocess") argumentsToSteps.add("preprocess");
            if (arg === "--embeddings") argumentsToSteps.add("embeddings");
            if (arg === "--analyze") argumentsToSteps.add("analyze");
            if (arg === "--prepare-queries")
              argumentsToSteps.add("prepare_queries");
            if (arg === "--execute-queries")
              argumentsToSteps.add("execute_queries");
            if (arg === "--populate-db") argumentsToSteps.add("populate_db");
          });

          // Note: Historical run step states are handled below

          // Update step states from historical run
          // IMPORTANT: Only overwrite step states if the pipeline is NOT actively running
          // If it's running, WebSocket messages will handle updates to avoid race conditions
          const historicalStepStates: Record<string, PipelineStepInfo> = {};
          PIPELINE_STEPS.forEach((step) => {
            const runStep = run.steps.find((s) => s.step_name === step.key);
            const validStatuses = [
              "pending",
              "started",
              "in_progress",
              "running",
              "completed",
              "error",
            ];
            const stepStatus =
              runStep?.status && validStatuses.includes(runStep.status)
                ? (runStep.status as PipelineStepInfo["status"])
                : "pending";

            historicalStepStates[step.key] = {
              step: step.key,
              display_name: step.display_name,
              status: stepStatus,
              message: runStep?.message || step.description,
              progress: runStep?.progress,
              error: runStep?.error,
              started_at: runStep?.started_at,
              completed_at: runStep?.completed_at,
            };
          });

          // Only set step states from historical data if pipeline is not actively running
          if (!isActivelyRunning) {
            setStepStates(historicalStepStates);
            logger.info(
              "📚 Loaded historical step states for completed/inactive pipeline",
            );
          } else {
            logger.info(
              "🔴 Skipping historical step state load - pipeline is actively running, WebSocket updates will handle state",
            );
          }

          // Detect completed phases from historical data
          const phase1Steps = PIPELINE_PHASES.PHASE_1.steps.map((s) => s.key);
          const phase2Steps = PIPELINE_PHASES.PHASE_2.steps.map((s) => s.key);

          // Check if Phase 1 is completed
          const phase1Completed = phase1Steps.every((stepKey) => {
            const stepState = historicalStepStates[stepKey];
            return stepState && stepState.status === "completed";
          });

          // Check if Phase 2 is completed
          const phase2Completed = phase2Steps.every((stepKey) => {
            const stepState = historicalStepStates[stepKey];
            return stepState && stepState.status === "completed";
          });

          // Check if analysis queries are available from stored data
          const analysisQueriesAvailable = Boolean(
            run.stored_data?.analysis_queries,
          );

          // Update phase status
          const newPhaseStatus = {
            phase_1_completed: phase1Completed,
            phase_2_completed: phase2Completed,
            analysis_queries_available: analysisQueriesAvailable,
          };

          logger.info("📊 Historical pipeline phase detection:", {
            pipelineId: run._id,
            phase1Completed,
            phase2Completed,
            analysisQueriesAvailable,
            phase1Steps: phase1Steps.map((key) => ({
              step: key,
              status: historicalStepStates[key]?.status || "not-found",
            })),
            phase2Steps: phase2Steps.map((key) => ({
              step: key,
              status: historicalStepStates[key]?.status || "not-found",
            })),
          });

          setPhaseStatus(newPhaseStatus);

          // Load analysis queries if available
          if (analysisQueriesAvailable && run.stored_data?.analysis_queries) {
            setAnalysisQueries(run.stored_data.analysis_queries);
            logger.info(
              `Loaded ${run.stored_data.analysis_queries.length} stored analysis queries for historical run`,
            );
          }

          // Update logs from historical run
          const historicalLogs = run.logs.map((log) => ({
            timestamp: new Date(log.timestamp).toLocaleTimeString(),
            message: log.message,
            level: log.level.toLowerCase() === "error" ? "error" : "info",
          }));
          setLogs(historicalLogs);

          // Set running state if pipeline is still active
          setIsRunning(run.status === "running" || run.status === "started");
        })
        .catch((error) => {
          logger.error("Failed to fetch pipeline data:", error);
          setError("Failed to load pipeline data.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isNewPipeline, pipelineId, token, apiBaseUrl]);

  // Handle WebSocket pipeline progress messages
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const messageData = JSON.parse(lastMessage.data);

      if (messageData.type === "pipeline_progress") {
        const progressMessage = messageData as PipelineProgressMessage;

        // Only process messages for our current pipeline or new pipelines
        if (
          !isNewPipeline &&
          currentPipelineId &&
          progressMessage.pipeline_id !== currentPipelineId
        ) {
          return;
        }

        // For new pipelines, accept any message until we have a currentPipelineId
        if (
          isNewPipeline &&
          currentPipelineId &&
          progressMessage.pipeline_id !== currentPipelineId
        ) {
          return;
        }

        // Update current pipeline ID if this is a new pipeline
        if (
          isNewPipeline &&
          progressMessage.pipeline_id &&
          progressMessage.pipeline_id !== currentPipelineId
        ) {
          setCurrentPipelineId(progressMessage.pipeline_id);
          // Navigate to the new pipeline URL
          navigate(`/pipeline/${progressMessage.pipeline_id}`, {
            replace: true,
          });
        }

        // Update step state - ensure we preserve existing step data and update correctly
        setStepStates((prev) => {
          const existingStep = prev[progressMessage.step];
          const stepConfig = PIPELINE_STEPS.find(
            (s) => s.key === progressMessage.step,
          );

          if (!existingStep && !stepConfig) {
            logger.warn(
              `Received update for unknown step: ${progressMessage.step}`,
            );
            return prev;
          }

          const updatedStep: PipelineStepInfo = {
            step: progressMessage.step,
            display_name:
              existingStep?.display_name ||
              stepConfig?.display_name ||
              progressMessage.step,
            status: progressMessage.status,
            message: progressMessage.message,
            progress: progressMessage.progress,
            error: progressMessage.error,
            // Preserve timing information
            started_at: existingStep?.started_at,
            completed_at:
              progressMessage.status === "completed"
                ? new Date().toISOString()
                : existingStep?.completed_at,
          };

          logger.info(
            `🔄 Updating step state: ${progressMessage.step} -> ${progressMessage.status}`,
          );

          return {
            ...prev,
            [progressMessage.step]: updatedStep,
          };
        });

        // Add to logs
        setLogs((prev) =>
          [
            ...prev,
            {
              timestamp: new Date(
                progressMessage.timestamp,
              ).toLocaleTimeString(),
              message: progressMessage.message,
              level: progressMessage.status === "error" ? "error" : "info",
            },
          ].slice(-100),
        ); // Keep last 100 entries

        // Check for phase completion
        if (progressMessage.status === "completed") {
          if (progressMessage.step === "prepare_queries") {
            // Phase 1 completed - fetch analysis queries and update UI state
            logger.info(
              "🎉 Phase 1 completed! Starting analysis queries fetch...",
            );
            setPhaseStatus((prev) => {
              const newStatus = {
                ...prev,
                phase_1_completed: true,
                analysis_queries_available: true,
              };
              logger.info(
                "📊 Phase status updated after Phase 1 completion:",
                newStatus,
              );
              return newStatus;
            });
            setCurrentPhase(null);
            setIsRunning(false); // Mark as no longer running so UI updates

            // Fetch analysis queries to enable Phase 2 button
            fetchAnalysisQueries(currentPipelineId);

            logger.info(
              "✅ Phase 1 UI state updated - Phase 2 button should now be visible",
            );
          } else if (progressMessage.step === "populate_db") {
            // Phase 2 completed (populate_db is the last step of Phase 2)
            logger.info("🎉 Phase 2 completed! Pipeline fully finished.");
            setPhaseStatus((prev) => {
              const newStatus = {
                ...prev,
                phase_2_completed: true,
              };
              logger.info(
                "📊 Phase status updated after Phase 2 completion:",
                newStatus,
              );
              return newStatus;
            });
            setCurrentPhase(null);
            setIsRunning(false);
          }
        }

        // Update running state
        if (
          progressMessage.status === "started" ||
          progressMessage.status === "running"
        ) {
          setIsRunning(true);

          // Detect which phase is running
          const phase1Steps = PIPELINE_PHASES.PHASE_1.steps.map((s) => s.key);
          const phase2Steps = PIPELINE_PHASES.PHASE_2.steps.map((s) => s.key);

          if (phase1Steps.includes(progressMessage.step)) {
            setCurrentPhase(1);
          } else if (phase2Steps.includes(progressMessage.step)) {
            setCurrentPhase(2);
          }
        } else if (progressMessage.status === "error") {
          setIsRunning(false);
          setCurrentPhase(null);
        }
        // Note: isRunning is set to false in phase completion blocks above

        logger.info("📥 Frontend received pipeline progress update:", {
          pipeline_id: progressMessage.pipeline_id,
          step: progressMessage.step,
          status: progressMessage.status,
          message: progressMessage.message,
          progress: progressMessage.progress,
          currentPipelineId: currentPipelineId,
          isNewPipeline: isNewPipeline,
          isViewingHistorical: !isNewPipeline,
        });
      }
    } catch (error) {
      logger.error("Failed to parse WebSocket message:", error);
    }
  }, [lastMessage, currentPipelineId, isNewPipeline, navigate]);

  // Fetch analysis queries from pipeline run
  const fetchAnalysisQueries = useCallback(
    async (pipelineId: string | null) => {
      if (!pipelineId || !token) return;

      try {
        const response = await axios.get<PipelineRunHistoryItem>(
          `${apiBaseUrl}/pipeline/history/${pipelineId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const storedData = response.data.stored_data;
        if (storedData?.analysis_queries) {
          setAnalysisQueries(storedData.analysis_queries);
          logger.info(
            `Loaded ${storedData.analysis_queries.length} analysis queries for phase review`,
          );
        }
      } catch (error) {
        logger.error("Failed to fetch analysis queries:", error);
        setError("Failed to load analysis queries for review");
      }
    },
    [apiBaseUrl, token],
  );

  // Trigger Phase 1 (Preparation)
  const triggerPhase1 = useCallback(async () => {
    if (!token) {
      setError("Authentication token not found. Please login.");
      return;
    }

    if (!isNewPipeline) {
      setError(
        "Cannot run pipeline while viewing historical data. Create a new pipeline run.",
      );
      return;
    }

    try {
      setError(null);
      setCurrentPhase(1);

      const triggerData: PipelineTriggerOptions = {
        run_fetch: true,
        run_preprocess: true,
        run_embeddings: true,
        run_analyze: true,
        run_prepare_queries: true,
        use_optimized_fetch: true,
        store_pipeline_data: true, // 🔑 Key flag for storing analysis queries
      };

      const response = await axios.post(
        `${apiBaseUrl}/pipeline/run`,
        triggerData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      logger.info(
        "Phase 1 (Preparation) triggered successfully:",
        response.data,
      );
      setLogs([
        {
          timestamp: new Date().toLocaleTimeString(),
          message:
            "Phase 1 (Preparation) initiated: fetch → preprocess → embeddings → analyze → prepare_queries",
          level: "info",
        },
      ]);
      setIsRunning(true);
    } catch (error: any) {
      logger.error("Failed to trigger Phase 1:", error);
      setError(error.response?.data?.detail || "Failed to trigger Phase 1");
      setCurrentPhase(null);
    }
  }, [token, apiBaseUrl, isNewPipeline]);

  // Trigger Phase 2 (Execution)
  const triggerPhase2 = useCallback(async () => {
    if (!token || !currentPipelineId) {
      setError("Authentication token or pipeline ID not found.");
      return;
    }

    if (!phaseStatus.phase_1_completed) {
      setError("Phase 1 must be completed before running Phase 2.");
      return;
    }

    try {
      setError(null);
      setCurrentPhase(2);

      const response = await axios.post(
        `${apiBaseUrl}/pipeline/resume/${currentPipelineId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      logger.info("Phase 2 (Execution) triggered successfully:", response.data);
      setLogs((prev) => [
        ...prev,
        {
          timestamp: new Date().toLocaleTimeString(),
          message:
            "Phase 2 (Execution) initiated: execute_queries → populate_db",
          level: "info",
        },
      ]);
      setIsRunning(true);
    } catch (error: any) {
      logger.error("Failed to trigger Phase 2:", error);
      setError(error.response?.data?.detail || "Failed to trigger Phase 2");
      setCurrentPhase(null);
    }
  }, [token, currentPipelineId, phaseStatus.phase_1_completed, apiBaseUrl]);

  // Resume pipeline (for incomplete runs)
  const resumePipeline = useCallback(async () => {
    if (!token || !currentPipelineId) {
      setError(
        "Cannot resume pipeline - missing authentication or pipeline ID.",
      );
      return;
    }

    setIsRunning(true);
    setError(null);

    try {
      logger.info("Resuming pipeline:", currentPipelineId);

      const response = await axios.post(
        `${apiBaseUrl}/pipeline/resume/${currentPipelineId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 202) {
        logger.info("Pipeline resume triggered successfully:", response.data);
      }
    } catch (error) {
      logger.error("Failed to resume pipeline:", error);
      setError(
        `Failed to resume pipeline: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      setIsRunning(false);
    }
  }, [token, currentPipelineId, apiBaseUrl]);

  // Check if pipeline can be resumed (legacy resume for incomplete pipelines)
  const canResume =
    !isNewPipeline &&
    pipelineData &&
    pipelineData.status !== "completed" &&
    pipelineData.status !== "failed" &&
    !phaseStatus.phase_1_completed && // Don't show legacy resume if Phase 1 is completed
    !isRunning;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="loading loading-lg loading-spinner"></span>
        <p className="ml-4 text-base-content/70">Loading pipeline...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/pipeline")}
            className="btn btn-ghost btn-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Pipeline Runs
          </button>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-3">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-base-content">
                {pipelineTitle}
              </h1>
              <p className="text-sm text-base-content/70">
                Data processing pipeline execution and monitoring
              </p>
            </div>
          </div>
        </div>

        {/* Pipeline Info (for existing pipelines) */}
        {!isNewPipeline && pipelineData && (
          <div className="text-right text-xs text-base-content/70">
            <div className="flex items-center justify-end gap-2">
              <User className="h-3 w-3" />
              <span>Run by {pipelineData.username}</span>
            </div>
            <div className="mt-1 flex items-center justify-end gap-2">
              <Calendar className="h-3 w-3" />
              <span>
                {formatDistanceToNow(new Date(pipelineData.triggered_at))} ago
              </span>
            </div>
            {pipelineData.duration_seconds && (
              <div className="mt-1 flex items-center justify-end gap-2">
                <Timer className="h-3 w-3" />
                <span>
                  {Math.round(pipelineData.duration_seconds)}s duration
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 alert alert-error">
          <AlertTriangle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Main Layout - Unified Design */}
      <div className="flex min-h-[calc(100vh-16rem)] gap-6">
        {/* Left Sidebar - Timeline */}
        <PipelineTimeline
          stepStates={stepStates}
          currentPhase={currentPhase}
          phaseStatus={phaseStatus}
        />

        {/* Main Content Area */}
        <div className="flex-1 space-y-6">
          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Phase 1 Button */}
            {!phaseStatus.phase_1_completed && (
              <button
                onClick={triggerPhase1}
                disabled={isRunning}
                className={`btn ${
                  currentPhase === 1 ? "loading btn-primary" : "btn-primary"
                }`}
              >
                {currentPhase === 1 ? (
                  "Running Phase 1..."
                ) : (
                  <>
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Start Phase 1
                  </>
                )}
              </button>
            )}

            {/* Phase 2 Button */}
            {phaseStatus.phase_1_completed &&
              !phaseStatus.phase_2_completed &&
              analysisQueries.length > 0 && (
                <button
                  onClick={triggerPhase2}
                  disabled={isRunning}
                  className={`btn ${
                    currentPhase === 2 ? "loading btn-success" : "btn-success"
                  }`}
                >
                  {currentPhase === 2 ? (
                    "Running Phase 2..."
                  ) : (
                    <>
                      <PlayCircle className="mr-2 h-4 w-4" />
                      Start Phase 2
                    </>
                  )}
                </button>
              )}

            {/* Legacy Resume Button */}
            {canResume && (
              <button
                onClick={resumePipeline}
                className="btn btn-warning"
                disabled={isRunning}
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                Resume Pipeline
              </button>
            )}
          </div>

          {/* Analysis Queries Display */}
          {phaseStatus.analysis_queries_available &&
          analysisQueries.length > 0 ? (
            <div className="flex-1">
              <AnalysisQueriesDisplay
                queries={analysisQueries}
                onClose={() => {}}
                showCloseButton={false}
              />
            </div>
          ) : phaseStatus.analysis_queries_available &&
            analysisQueries.length === 0 ? (
            <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-base-300">
              <div className="text-center">
                <Activity className="mx-auto mb-4 h-12 w-12 text-base-content/30" />
                <h3 className="mb-2 text-lg font-semibold text-base-content/70">
                  No analysis queries from pipeline
                </h3>
                <p className="text-sm text-base-content/70">
                  Has the pipeline already been completed for the latest
                  release?
                </p>
              </div>
            </div>
          ) : null}

          {/* Empty State - Before Phase 1 */}
          {!phaseStatus.phase_1_completed && !currentPhase && (
            <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-base-300">
              <div className="text-center">
                <Activity className="mx-auto mb-4 h-12 w-12 text-base-content/30" />
                <h3 className="mb-2 text-lg font-semibold text-base-content/70">
                  Ready to Start Pipeline
                </h3>
                <p className="text-sm text-base-content/50">
                  Click "Start Phase 1" to begin data processing
                </p>
              </div>
            </div>
          )}

          {/* Running State - During Phase 1 */}
          {currentPhase === 1 && !phaseStatus.analysis_queries_available && (
            <div className="flex h-64 items-center justify-center rounded-lg border border-primary/20 bg-primary/5">
              <div className="text-center">
                <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
                <h3 className="mb-2 text-lg font-semibold text-primary">
                  Phase 1 Running
                </h3>
                <p className="text-sm text-base-content/70">
                  Processing data and preparing analysis queries...
                </p>
              </div>
            </div>
          )}

          {/* Running State - During Phase 2 */}
          {currentPhase === 2 && (
            <div className="rounded-lg border border-success/20 bg-success/5 p-6">
              <div className="mb-4 flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-success" />
                <h3 className="text-lg font-semibold text-success">
                  Phase 2 Executing
                </h3>
              </div>
              <p className="text-sm text-base-content/70">
                Executing AI queries and populating the database...
              </p>
            </div>
          )}

          {/* Completion State */}
          {phaseStatus.phase_2_completed && (
            <div className="rounded-lg border border-success/20 bg-success/5 p-6">
              <div className="mb-4 flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-success" />
                <h3 className="text-lg font-semibold text-success">
                  Pipeline Completed
                </h3>
              </div>
              <p className="text-sm text-base-content/70">
                All pipeline phases have been completed successfully.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Logs - Collapsible */}
      <div className="mt-8">
        <details className="rounded-lg border border-base-200 bg-base-100">
          <summary className="hover:bg-base-50 flex cursor-pointer items-center gap-2 p-4 text-sm font-medium text-base-content/80">
            <Activity className="h-4 w-4" />
            Log ({logs.length})
          </summary>
          <div className="border-t border-base-200 p-4">
            <div className="h-64 overflow-hidden">
              {logs.length === 0 ? (
                <div className="flex h-full items-center justify-center text-base-content/50">
                  <div className="text-center">
                    <Activity className="mx-auto mb-2 h-6 w-6 opacity-50" />
                    <p className="text-sm">
                      Pipeline logs will appear here when running
                    </p>
                  </div>
                </div>
              ) : (
                <div className="h-full space-y-1 overflow-y-auto font-mono text-xs">
                  {logs.map((log, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${
                        log.level === "error"
                          ? "text-error"
                          : "text-base-content/70"
                      }`}
                    >
                      <span className="flex-shrink-0 font-mono text-xs text-base-content/50">
                        {log.timestamp}
                      </span>
                      <span className="font-mono break-all">{log.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

export default Pipeline;
